<template>
    <div>
        <page-header> Phone Numbers </page-header>

        <team-phone-numbers-controls
            v-model="fetchParams"
            :contactGroups="FEATURES.contact_groups"
            @apply="applyFilters"
            @reset="resetFilters"
            :loading="loading"
        >
            <template v-if="onCorporate() && isImpersonatingOrPlatformAdmin" v-slot:right>
                <div class="grid grid-cols-2 gap-4">
                    <vx-button size="lg" @click="showHostNumberModal" color="muted-dark" data-test="add-hosted-number">
                        Add Hosted Number
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>
                    <vx-button
                        :href="`/settings/teams/${teamId}/phone-numbers/new`"
                        size="lg"
                        color="primary"
                        data-test="add-phone-number"
                    >
                        Add Phone Number
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>
                </div>
            </template>
        </team-phone-numbers-controls>

        <vx-alert v-if="addedToPool" color="success" :closeable="false" shadow>
            <template v-slot:icon>
                <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
            </template>
            <div class="flex justify-between items-center">
                <div>
                    <strong>{{ formatPhoneNumber(addedToPool.phoneNumber.phone_number) }}</strong> added to
                    <strong>{{ addedToPool.phonePool.label }}</strong>
                </div>
                <vx-button
                    :href="`/settings/teams/${teamId}/phone-number-pools/${addedToPool.phonePool.value}`"
                    color="success"
                    size="sm"
                >
                    View
                    <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                </vx-button>
            </div>
        </vx-alert>

        <template v-if="!loading && !list.length">
            <empty-state v-if="!isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />
            <empty-state v-else>
                No Phone Numbers yet.
                <vx-button
                    v-if="onCorporate() && isImpersonatingOrPlatformAdmin"
                    :href="`/settings/teams/${teamId}/phone-numbers/new`"
                >
                    Add Phone Number
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </empty-state>
        </template>

        <vx-table
            v-else-if="list.length"
            :class="{
                'xl:grid-cols-6-auto': !FEATURES.contact_groups && !isImpersonatingOrPlatformAdmin,
                'xl:grid-cols-7-auto':
                    (FEATURES.contact_groups && !isImpersonatingOrPlatformAdmin) ||
                    (!FEATURES.contact_groups && isImpersonatingOrPlatformAdmin),
                'xl:grid-cols-8-auto': FEATURES.contact_groups && isImpersonatingOrPlatformAdmin,
            }"
            rowBreakpoint="xl"
        >
            <vx-table-row isHeader>
                <div>Number</div>
                <div>Number Type</div>
                <div>Country</div>
                <div>Voice</div>
                <div>Sending</div>
                <div v-if="FEATURES.contact_groups">Group</div>
                <div v-if="isImpersonatingOrPlatformAdmin">Vendor</div>
            </vx-table-row>

            <team-phone-number-row
                v-for="(item, index) in list"
                v-model="list[index]"
                :key="list[index].id"
                @deleted="removeFromList($event)"
                @addedToPool="onAddedToPool"
            ></team-phone-number-row>
        </vx-table>

        <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
            <vx-button
                v-if="fetchParams.cursor"
                @click="loadData()"
                color="muted-dark"
                size="xl"
                data-test="load-more-btn"
                class="grow-0"
                :loading="loading"
            >
                Load More
            </vx-button>
        </div>

        <host-phone-number-modal
            v-model:visible="visibleHostNumberModal"
            @save="onHostNumberSave"
        ></host-phone-number-modal>
    </div>
</template>

<script setup>
import { faCircleCheck, faCirclePlus, faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageHeader from '../../../general/PageHeader.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import { VxAlert, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { onMounted, ref, inject } from 'vue';
import { useCursorPagination } from '../../../../composables/useCursorPagination';
import TeamPhoneNumberRow from './TeamPhoneNumberRow.vue';
import TeamPhoneNumbersControls from './TeamPhoneNumbersControls.vue';
import { phoneNumbersClient } from '~/services';
import HostPhoneNumberModal from './HostPhoneNumberModal.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { isEqual } from '~/utils/helpers';
import { formatPhoneNumber } from '~/components/filters';

const FEATURES = inject('FEATURES');

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const teamId = Spark.state.currentTeam.id;
const isImpersonatingOrPlatformAdmin = Spark.isImpersonating || Spark.isPlatformAdmin;
const { onCorporate } = useCurrentGroup();

const visibleHostNumberModal = ref(false);

const defaultFilters = () => ({
    per_page: 10,
    cursor: null,
    sort_field: 'id',
    sort_dir: 'desc',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

onMounted(() => {
    resetFilters();
});

const loadData = () => {
    fetchRecords(phoneNumbersClient.listPhoneNumbers(teamId, { ...fetchParams.value }));
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.id != itemId);

    if (!list.value.length) {
        loadData();
    }
};

const addedToPool = ref();
const onAddedToPool = (phoneNumber, phonePool) => {
    addedToPool.value = { phoneNumber, phonePool };
};

const showHostNumberModal = () => {
    visibleHostNumberModal.value = true;
};

const onHostNumberSave = () => {
    visibleHostNumberModal.value = false;
    fetchParams.value.cursor = null;

    loadData();
};
</script>
